import React, { useState } from 'react';
import propTypes from 'prop-types';
import styles from './index.module.sass';
import { ChevronDownIcon, CloseIcon } from 'icons';
import classNames from 'classnames/bind';
import DataList from '../data-list';

const cx = classNames.bind(styles);

/**
 * @typedef DataItem
 *
 * @property {string} label - label
 * @property {string} value - value
 * @property {string} [subLabel] - subLabel will show inside the DataList, but won't show in the input
 */

/**
 * @callback onSelect
 *
 * @param {string} value - selected value
 * @returns {undefined}
 */

/**
 * ### Select
 *
 * @param {{string | null}} value - selected value
 * @param {string} className - custom class name
 * @param {Array<DataItem>} data - { label: string; value: string; subLabel: string }
 * @param {onSelect} onSelect - callback function to get the selected value
 * @param {boolean} disabled - disabled
 * @param {string} placeholder - placeholder
 * @param {string} errorMessage - error message
 * @param {function} onClear - clear selected value
 * @param {'bottom-left' | 'bottom-right'} placement - placement
 *
 * @returns
 */

const Select = ({
  value,
  className = '',
  errorMessage = undefined,
  data,
  onSelect,
  disabled,
  placeholder,
  onClear,
  placement = 'bottom-left',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const currentLabel = data.find(x => x.value === value)?.label;

  const selectorClass = cx({
    selector: true,
    active: showDropdown,
    invalid: !!errorMessage,
    disabled: disabled,
  });

  const dataListClass = cx({
    [placement]: true,
  });

  const handleSelectHandler = key => {
    setShowDropdown(false);
    onSelect(key);
  };

  const handleCloseIconClick = e => {
    if (showDropdown) return;
    e.stopPropagation();
    onClear();
  };

  return (
    <div className={`${styles.select} ${className}`} tabIndex={-1} onBlur={() => setShowDropdown(false)}>
      <div className={selectorClass} onClick={() => setShowDropdown(!showDropdown)}>
        {currentLabel ? (
          <span className={styles.value}>{currentLabel}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
        {currentLabel ? (
          <CloseIcon onClick={handleCloseIconClick} width={24} height={24} />
        ) : (
          <ChevronDownIcon className={styles['chevron-icon']} width={24} height={24} />
        )}
      </div>
      {errorMessage && <small className={styles['error-message']}>{errorMessage}</small>}
      <DataList
        className={dataListClass}
        value={value}
        data={data.map(({ label, value, subLabel }) => ({
          label: subLabel ? `${label} (${subLabel})` : label,
          value,
        }))}
        show={showDropdown}
        onSelect={handleSelectHandler}
      />
    </div>
  );
};

Select.propTypes = {
  value: propTypes.string,
  className: propTypes.string,
  placeholder: propTypes.string,
  data: propTypes.array,
  onSelect: propTypes.func,
  disabled: propTypes.bool,
};

export default Select;
